/**
 * Production ErrorBoundary — pattern aligned with @pavanhiro/common-ui
 * (lightweight port; avoids bundling the full common-ui graph).
 */
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from './Button'

interface Props {
  children: ReactNode
  boundaryName?: string
  fallbackTitle?: string
}

interface State {
  hasError: boolean
  error: Error | null
  errorCount: number
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, errorCount: 0 }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const name = this.props.boundaryName || 'App'
    console.error(`[Flowline:${name}]`, error, info.componentStack)
    this.setState((s) => ({ errorCount: s.errorCount + 1 }))
  }

  private retry = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (!this.state.hasError) return this.props.children

    const title = this.props.fallbackTitle || 'Something went wrong'
    const detail = this.state.error?.message || 'Unexpected UI error'

    return (
      <div className="error-fallback" role="alert">
        <h2>{title}</h2>
        <p className="row-meta">{detail}</p>
        {this.state.errorCount > 2 ? (
          <p className="row-meta">Repeated failures — try a hard refresh.</p>
        ) : null}
        <div className="hero-cta" style={{ marginTop: 14 }}>
          <Button variant="primary" onClick={this.retry}>
            Try again
          </Button>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </div>
      </div>
    )
  }
}
